import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard,
  Crown,
  Calendar,
  Users,
  FileText,
  Link2,
  Zap,
  RefreshCw,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Clock,
  Lock,
  ArrowUpRight,
  Mail,
  Phone,
  Palette,
  Globe,
  Receipt,
  DollarSign,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import AccountClassificationSection from './AccountClassificationSection';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  trial: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  past_due: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
  suspended: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', icon: AlertTriangle },
};

// Feature items for the Enabled Features table
const FEATURE_ITEMS = [
  { key: 'mailgun', name: 'Mailgun Email Service', icon: Mail, entitlement: 'mailgun_email' },
  { key: 'twilio', name: 'Twilio Phone Service', icon: Phone, entitlement: 'twilio_phone' },
  { key: 'custom_domain', name: 'Custom Domain', icon: Globe, entitlement: 'custom_domain' },
  { key: 'portal_branding', name: 'Portal Branding', icon: Palette, entitlement: 'portal_branding' },
  { key: 'white_label', name: 'White Label / Rebrand', icon: Crown, entitlement: 'white_label' },
  { key: 'private_integrations', name: 'Private Integrations', icon: Lock, entitlement: 'api_access' },
];

/**
 * Billing & Membership Section
 * 
 * Shows agency's current plan, membership status, usage counters, and limits.
 * Contains the official "Upgrade / Add Features" CTA.
 * 
 * IMPORTANT: This is the commercial/entitlement layer.
 * Technical setup for Mailgun/Twilio belongs in Communication Settings.
 */
const BillingMembershipSection = ({ agency, agencyId, entitlements, isReadOnly, isPlatformAdmin, navigateToSection }) => {
  const { token } = useAuth();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classificationPlan, setClassificationPlan] = useState(null);
  const membershipRequestSeq = useRef(0);
  const fetchMembership = useCallback(async () => {
    const seq = ++membershipRequestSeq.current;
    setLoading(true);
    setMembership(null);
    try {
      const response = await axios.get(
        `${API}/admin/agencies/${agencyId}/membership`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (seq !== membershipRequestSeq.current) return;
      setMembership(response.data);
    } catch (err) {
      if (seq !== membershipRequestSeq.current) return;
      console.error('Failed to fetch membership:', err);
    } finally {
      if (seq === membershipRequestSeq.current) setLoading(false);
    }
  }, [agencyId, token]);
  
  useEffect(() => {
    if (token && agencyId) {
      setClassificationPlan(null);
      fetchMembership();
    }
  }, [token, agencyId, fetchMembership]);

  const handleConfigurationSaved = useCallback((classification, options = {}) => {
    if (!classification) {
      setClassificationPlan(null);
      return;
    }
    setClassificationPlan({
      plan_name: classification.plan_name || null,
      plan_price: classification.plan_price ?? null,
      status: classification.status || null,
      billing_cycle: classification.billing_cycle || 'Monthly',
      is_classified: Boolean(classification.is_classified || classification.plan_id),
    });
    // Soft GET notifications must not re-fetch membership (that remounts/rerenders
    // and previously fed an infinite classification reload loop).
    if (!options.soft) {
      fetchMembership();
    }
  }, [fetchMembership]);
  
  // Check if a feature is enabled
  const isFeatureEnabled = (entitlementKey) => {
    const mode = entitlements[entitlementKey];
    return mode && mode === 'full_access';
  };
  
  // Get feature status
  const getFeatureStatus = (entitlementKey) => {
    const mode = entitlements[entitlementKey];
    if (mode === 'full_access') return 'enabled';
    if (mode === 'locked') return 'locked';
    return 'not_enabled';
  };

  const displayPlanName = classificationPlan?.is_classified
    ? (classificationPlan.plan_name || 'No plan configured')
    : (membership?.plan_name || null);
  const displayPlanPrice = classificationPlan?.is_classified
    ? classificationPlan.plan_price
    : membership?.plan_price;
  const displayStatus = classificationPlan?.is_classified
    ? (classificationPlan.status || membership?.status || null)
    : (membership?.status || null);
  const hasConfiguredPlan = Boolean(
    (classificationPlan?.is_classified && classificationPlan?.plan_name)
    || (!classificationPlan?.is_classified && membership?.plan_name)
  );
  
  const status = displayStatus || 'active';
  const StatusIcon = STATUS_STYLES[status]?.icon || CheckCircle;
  const membershipLoading = loading && !membership && !classificationPlan;

  return (
    <div className="space-y-6" data-testid="billing-membership-section">
      {/* Section Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <CreditCard className="text-emerald-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Billing & Membership</h2>
            <p className="text-sm text-slate-500">
              Subscription, entitlements, usage, and upgrade options
            </p>
          </div>
        </div>
      </div>

      {/* 1. Account Configuration — always mount for platform admins (stable key).
          Do not gate on membership loading; that previously delayed mount and
          combined with an unstable callback to create a fetch/render loop. */}
      {isPlatformAdmin ? (
        <AccountClassificationSection
          key={agencyId}
          agencyId={agencyId}
          token={token}
          isPlatformAdmin={isPlatformAdmin}
          isReadOnly={isReadOnly}
          onConfigurationSaved={handleConfigurationSaved}
        />
      ) : null}

      {membershipLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
        </div>
      ) : null}

      {/* 2. Current Plan Card */}
      <div
        className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-6 text-white"
        data-testid="current-plan-card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
              <Crown className="text-white" size={28} />
            </div>
            <div>
              <p className="text-amber-100 text-sm">Current Plan</p>
              <h3 className="text-2xl font-bold" data-testid="current-plan-name">
                {hasConfiguredPlan ? displayPlanName : 'No plan configured'}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                {hasConfiguredPlan ? (
                  <Badge className={`${STATUS_STYLES[status]?.bg || 'bg-white/20'} ${STATUS_STYLES[status]?.text || 'text-white'} border-0`}>
                    <StatusIcon size={12} className="mr-1" />
                    {String(status).charAt(0).toUpperCase() + String(status).slice(1).replace('_', ' ')}
                  </Badge>
                ) : (
                  <Badge className="bg-white/20 text-white border-0">No active plan</Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-amber-100 text-sm">
              {classificationPlan?.billing_cycle || membership?.billing_cycle || 'Monthly'}
            </p>
            <p className="text-3xl font-bold" data-testid="current-plan-price">
              {hasConfiguredPlan && displayPlanPrice != null ? `$${displayPlanPrice}` : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* 3. Upgrade / Add Features */}
      <div className="flex justify-end">
        <Button
          className="bg-amber-500 hover:bg-amber-600 text-white font-semibold"
          data-testid="upgrade-add-features-btn"
        >
          <ArrowUpRight size={16} className="mr-2" />
          Upgrade / Add Features
        </Button>
      </div>

      {/* 4. Enabled Features / Add-ons Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Enabled Features & Add-ons</h3>
              <p className="text-sm text-slate-500">Communication services and premium capabilities included in your plan</p>
            </div>
          </div>
        </div>
        
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Feature</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {FEATURE_ITEMS.map((feature) => {
              const Icon = feature.icon;
              const featureStatus = getFeatureStatus(feature.entitlement);
              
              return (
                <tr key={feature.key} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Icon size={18} className="text-slate-600" />
                      </div>
                      <span className="font-medium text-slate-900">{feature.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {featureStatus === 'enabled' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0">
                        <CheckCircle size={12} className="mr-1" />
                        Enabled
                      </Badge>
                    ) : featureStatus === 'locked' ? (
                      <Badge className="bg-slate-100 text-slate-500 border-0">
                        <Lock size={12} className="mr-1" />
                        Locked
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-0">
                        <AlertTriangle size={12} className="mr-1" />
                        Not Enabled
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {featureStatus === 'enabled' ? (
                      <span className="text-sm text-slate-500">Included</span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      >
                        Upgrade / Add Features
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Subscription Summary */}
      <div className="grid grid-cols-2 gap-6">
        {/* Membership Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Receipt size={18} className="text-slate-400" />
            Subscription Summary
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Membership ID</span>
              <span className="text-sm font-mono text-slate-700">{membership?.membership_id || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Billing Cycle</span>
              <span className="text-sm text-slate-700">{membership?.billing_cycle || 'Monthly'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Next Renewal</span>
              <span className="text-sm text-slate-700">
                {membership?.next_renewal_date 
                  ? new Date(membership.next_renewal_date).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-500">Payment Method</span>
              <span className="text-sm text-slate-700">Visa ending in 4021</span>
            </div>
          </div>
        </div>
        
        {/* Billing Actions */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <DollarSign size={18} className="text-slate-400" />
            Billing Actions
          </h3>
          
          <div className="space-y-3">
            <Button variant="outline" className="w-full justify-start" disabled={isReadOnly}>
              <CreditCard size={16} className="mr-3" />
              Update Payment Method
            </Button>
            <Button variant="outline" className="w-full justify-start" disabled={isReadOnly}>
              <Receipt size={16} className="mr-3" />
              View Invoices
            </Button>
            <Button variant="outline" className="w-full justify-start" disabled={isReadOnly}>
              <RefreshCw size={16} className="mr-3" />
              Change Billing Cycle
            </Button>
          </div>
        </div>
      </div>
      
      {/* Usage Counters */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp size={18} className="text-slate-400" />
            Usage & Limits
          </h3>
          <Badge variant="outline" className="text-slate-500">
            Current Period
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Seats */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">User Seats</span>
              </div>
              <span className="text-sm text-slate-600">
                {membership?.usage?.seats_used || 3} / {membership?.limits?.max_seats || 10}
              </span>
            </div>
            <Progress 
              value={membership?.limits?.max_seats 
                ? ((membership?.usage?.seats_used || 3) / (membership?.limits?.max_seats || 10)) * 100 
                : 30
              } 
              className="h-2"
            />
          </div>
          
          {/* Letter Credits */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Letter Credits</span>
              </div>
              <span className="text-sm text-slate-600">
                {membership?.usage?.letters_used || 45} / {membership?.limits?.letters_per_month || 100}
              </span>
            </div>
            <Progress 
              value={membership?.limits?.letters_per_month 
                ? ((membership?.usage?.letters_used || 45) / (membership?.limits?.letters_per_month || 100)) * 100 
                : 45
              } 
              className="h-2"
            />
          </div>
          
          {/* Report Imports */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Report Imports</span>
              </div>
              <span className="text-sm text-slate-600">
                {membership?.usage?.imports_used || 12} / {membership?.limits?.imports_per_month || 50}
              </span>
            </div>
            <Progress 
              value={membership?.limits?.imports_per_month 
                ? ((membership?.usage?.imports_used || 12) / (membership?.limits?.imports_per_month || 50)) * 100 
                : 24
              } 
              className="h-2"
            />
          </div>
          
          {/* Provider Connections */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link2 size={16} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Provider Connections</span>
              </div>
              <span className="text-sm text-slate-600">
                {membership?.usage?.connections_used || 2} / {membership?.limits?.max_connections || 5}
              </span>
            </div>
            <Progress 
              value={membership?.limits?.max_connections 
                ? ((membership?.usage?.connections_used || 2) / (membership?.limits?.max_connections || 5)) * 100 
                : 40
              } 
              className="h-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingMembershipSection;
